import { IonButton, IonIcon, IonText, useIonRouter } from '@ionic/react';
import { createSelector } from '@reduxjs/toolkit';
import { business } from 'ionicons/icons';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { msgid, ngettext, t } from 'ttag';

import ContentGeneric from '@components/Content';
import Layout from '@components/Layout';
import RoutePath from '@custom-types/RoutePath';
import useLoading from '@hooks/useLoading';
import { useLeadCount } from '@pages/LeadCapture/queries';
import { generateRoute } from '@services/navigation-service';
import { getCompanyInfo } from '@state/company';
import { getEventInfo } from '@state/event';
import { getPeopleId } from '@state/user';

const Content = styled(ContentGeneric)`
  padding: 0 18px;
  justify-content: center;
`;

const Title = styled(IonText)`
  font-size: 48px;
  font-weight: 700;
  margin-bottom: 16px;
`;

const Subtitle = styled(IonText)`
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 8px;
`;

const Icon = styled(IonIcon)`
  margin-right: 9px;
`;

const Button = styled(IonButton)`
  margin-top: 42px;
  width: 100%;
`;

const selector = createSelector(
  getEventInfo,
  getPeopleId,
  getCompanyInfo,
  ({ id: eventId, name: eventName }, peopleId, { id: companyId, name: companyName }) => ({
    eventId,
    eventName,
    peopleId,
    companyId,
    companyName,
  })
);

const Home = () => {
  const { push: pushRoute } = useIonRouter();
  const { eventId, eventName, peopleId, companyId, companyName } = useSelector(selector);
  const { isLoading, data } = useLeadCount({
    eventId,
    companyId,
    params: {
      capturedByPerson: peopleId,
    },
  });
  useLoading(isLoading);

  const numLeads = data ?? 0;

  const handleViewListNavigation = useCallback(
    () => pushRoute(generateRoute(RoutePath.LeadList), 'forward', 'replace'),
    [pushRoute]
  );
  // skipcq
  const subtitle = ngettext(msgid`Lead Collected`, `Leads Collected`, numLeads);
  return (
    <Layout title={eventName}>
      <Content>
        <Title>{numLeads}</Title>
        <Subtitle>{subtitle}</Subtitle>
        <IonText color="medium">
          <Icon icon={business} />
          {companyName}
        </IonText>
        {/* use 'replace' as RouterAction in router.push to unmount this page when navigating away, in order to fetch/mount again when navigate back */}
        <Button role="button" onClick={handleViewListNavigation}>{t`View List`}</Button>
      </Content>
    </Layout>
  );
};

export default Home;
